import { useCallback, useEffect, useRef, useState } from 'react';
import type { FuseSortFunction, FuseSortFunctionArg } from 'fuse.js';

import type { QuickInsertItem } from '@atlaskit/editor-common/provider-factory';

import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';

import {
	getAllElementInsertionStats,
	incrementElementCount,
} from '../utils/elementInsertionsStats';

enum SmartElementRecommendationsCohorts {
	NOT_ENROLLED = 'not-enrolled',
	CONTROL = 'control',
	VARIANT1 = 'variant1',
}

export const getMacroToPriorityMap = (multiplier: number): Map<string, number> => {
	const map = new Map<string, number>();

	const elementUsageStats = getAllElementInsertionStats();

	for (const elementUsage of elementUsageStats) {
		const priority = 1 / (elementUsage.count * multiplier);
		map.set(elementUsage.element, priority);
	}

	return map;
};

export const useSmartElementRecommendations = () => {
	const [forceRebuildMap, setForceRebuildMap] = useState<boolean>(false);
	const macroToPriorityRef = useRef(new Map<string, number>());
	const hasFiredExperimentExposure = useRef<boolean>(false);
	// eslint-disable-next-line confluence-feature-gating/unsafe-no-exposure
	const [experimentConfig, fireExposure] = UNSAFE_noExposureExp(
		'confluence_frontend_smart_element_recommendations',
	);
	const cohort = experimentConfig.get<SmartElementRecommendationsCohorts>(
		'cohort',
		SmartElementRecommendationsCohorts.NOT_ENROLLED,
	) as SmartElementRecommendationsCohorts;

	const fireExperimentExposure = useCallback(() => {
		if (hasFiredExperimentExposure.current) return;
		hasFiredExperimentExposure.current = true;

		fireExposure();
	}, [fireExposure]);

	useEffect(() => {
		// This multiplier is used to scale the priority of the elements. Since it may take
		// a while for element usage stats to accumulate, we want to give the elements that
		// users have interacted with a higher priority. In effect, the multiplier makes the
		// ordering more sensitive to the element usage stats.
		const multiplier = StatsigConfigurations.getNumber(
			'confluence_frontend_smart_element_multiplier',
			1,
		);

		const map = getMacroToPriorityMap(multiplier);
		macroToPriorityRef.current = map;
	}, [forceRebuildMap]);

	const prioritySortingFn = useCallback(
		(items: QuickInsertItem[]) => {
			fireExperimentExposure();

			return (a: FuseSortFunctionArg, b: FuseSortFunctionArg): number => {
				const aTitle = items[a.idx]?.title;
				const bTitle = items[b.idx]?.title;

				const aPriority = macroToPriorityRef.current.get(aTitle) ?? 1;
				const bPriority = macroToPriorityRef.current.get(bTitle) ?? 1;

				const adjustedScoreA = (a.score ?? 1) * aPriority;
				const adjustedScoreB = (b.score ?? 1) * bPriority;

				return adjustedScoreA - adjustedScoreB;
			};
		},
		[fireExperimentExposure],
	);

	const controlFnToTriggerExposure = useCallback(
		(_: QuickInsertItem[]) => {
			fireExperimentExposure();

			// Users in the variant1 cohort will use the prioritySortingFn which
			// returns the actual function that gets passed in as the sortFn in
			// the Fuse options. This function is used by control group so that we
			// can still trigger the exposure event when the user opens the element
			// list, but we don't want to actually change the sort method so we return
			// undefined here.
			return undefined;
		},
		[fireExperimentExposure],
	);

	const handleInsert = useCallback((item: QuickInsertItem) => {
		if (incrementElementCount(item)) {
			setForceRebuildMap((prev) => !prev);
		}
	}, []);

	let sortFn: ((items: QuickInsertItem[]) => FuseSortFunction | undefined) | undefined = undefined;
	if (cohort === SmartElementRecommendationsCohorts.CONTROL) {
		sortFn = controlFnToTriggerExposure;
	} else if (cohort === SmartElementRecommendationsCohorts.VARIANT1) {
		sortFn = prioritySortingFn;
	}

	return {
		prioritySortingFn: sortFn,
		handleInsert,
	};
};
